import { createPageUrl } from '@/lib/utils';
import { ArrowUpRight, Facebook, Github, Linkedin, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import logo from '../../assets/primelogictechsolutions.webp';

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const services = [
    'Software Development',
    'Web Applications',
    'Mobile Apps',
    'Cloud Solutions',
    'AI & Automation',
    'DevOps & Security',
  ];

  const quickLinks = [
    { name: 'Home', path: 'Home' },
    { name: 'About Us', path: 'AboutUs' },
    { name: 'Services', path: 'Services' },
    { name: 'Contact', path: 'Contact' },
  ];

  return (
    <footer className="bg-primary border-t border-primary-foreground/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="space-y-6">
            <Link to={createPageUrl('Home')} className="flex items-center gap-3">
              <img
                src={logo}
                alt="PrimeLogic Logo"
                className="h-8 w-auto"
                width="203"
                height="32"
                loading="lazy"
              />
            </Link>
            <p className="text-primary-foreground/70 text-sm leading-relaxed">
              Delivering intelligent, scalable, and reliable digital solutions that help businesses
              innovate, grow, and perform better.
            </p>
            <div className="flex items-center gap-4">
              <a
                href="https://www.linkedin.com/company/primelogic-tech-solutions"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PrimeLogic Tech Solutions on LinkedIn (opens in new tab)"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-secondary/20 rounded-lg flex items-center justify-center transition-colors group"
              >
                <Linkedin
                  className="w-5 h-5 text-primary-foreground/70 group-hover:text-secondary"
                  aria-hidden="true"
                />
              </a>
              <a
                href="https://github.com/PrimeLogic-Tech-Solutions"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PrimeLogic on GitHub (opens in new tab)"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-secondary/20 rounded-lg flex items-center justify-center transition-colors group"
              >
                <Github
                  className="w-5 h-5 text-primary-foreground/70 group-hover:text-secondary"
                  aria-hidden="true"
                />
              </a>
              <a
                href="https://x.com/motari_kepha"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PrimeLogic on X / Twitter (opens in new tab)"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-secondary/20 rounded-lg flex items-center justify-center transition-colors group"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-5 h-5 text-primary-foreground/70 group-hover:text-secondary transition-colors"
                  aria-hidden="true"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
              <a
                href="https://www.facebook.com/kepha.motari"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="PrimeLogic Tech Solutions on Facebook (opens in new tab)"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-secondary/20 rounded-lg flex items-center justify-center transition-colors group"
              >
                <Facebook
                  className="w-5 h-5 text-primary-foreground/70 group-hover:text-secondary"
                  aria-hidden="true"
                />
              </a>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-primary-foreground font-semibold mb-6">Services</h3>
            <ul className="space-y-3">
              {services.map((service) => {
                const serviceId = service
                  .toLowerCase()
                  .replace(/ & /g, '-and-')
                  .replace(/\s+/g, '-');
                return (
                  <li key={service}>
                    <Link
                      to={`${createPageUrl('Services')}#${serviceId}`}
                      className="text-primary-foreground/70 hover:text-secondary transition-colors text-sm flex items-center gap-2 group"
                    >
                      {service}
                      <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-primary-foreground font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={createPageUrl(link.path)}
                    className="text-primary-foreground/70 hover:text-secondary transition-colors text-sm flex items-center gap-2 group"
                  >
                    {link.name}
                    <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-primary-foreground font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <span className="text-primary-foreground/70 text-sm">Nairobi, Kenya</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-secondary flex-shrink-0" />
                <a
                  href="tel:+254721286785"
                  className="text-primary-foreground/70 hover:text-secondary text-sm transition-colors"
                >
                  +254 721 286 785
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-secondary flex-shrink-0" />
                <a
                  href="mailto:info@primelogic.ke"
                  className="text-primary-foreground/70 hover:text-secondary text-sm transition-colors"
                >
                  info@primelogic.ke
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/10 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-primary-foreground/80 text-sm">
            © {currentYear} PrimeLogic Tech Solutions. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="#"
              className="text-primary-foreground/80 hover:text-primary-foreground text-sm transition-colors"
            >
              Privacy Policy
            </a>
            <a
              href="#"
              className="text-primary-foreground/80 hover:text-primary-foreground text-sm transition-colors"
            >
              Terms of Service
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
